import React from "react";
import { FormattedHTMLMessage, injectIntl } from "react-intl";
import produce from "immer";
import {
  FormSelectChoice,
  FormFieldComponentProps,
  LowIncomePersonalInfoViewModel,
  Organization,
} from "../../../lib/types/types";
import { FormFieldInputSelect } from "../FormFieldInputSelect/FormFieldInputSelect";
import { LoadingSpinnerComponent } from "../../LoadingSpinner/LoadingSpinnerComponent";
import { getOrgSearchUrl } from "../../../lib/ProgramTheme/programThemeGetters";
import { fetchOrganization } from "../../../lib/ServerApi/OrganizationApiClient";
import { logger } from "../../../lib/utils/logger/logger";
import {
  getFieldConfigByFieldId,
  orgToOption,
} from "../../../lib/utils/stepComponentHelpers/stepComponentHelpers";
import { FormFieldTypeahead } from "../FormFieldTypeahead/FormFieldTypeahead";
import { isLowIncomeVM } from "../../StepLowIncomePersonalInfo/lowIncomeTypes";

export const LowIncomeOrganization = ({
  value,
  isErrored,
  onChange,
  intl,
  placeholder = "",
  isRequired = false,
  verificationService,
  disabled = false,
}: FormFieldComponentProps) => {
  const { viewModel, programTheme, fieldValidationErrors } = verificationService;

  if (!isLowIncomeVM(viewModel)) {
    logger.error("Expected low-income view model is malformed");
    return null;
  }

  const selectedStatus = viewModel.statuses[0] ? viewModel.statuses[0] : undefined;
  const [otherGovAssistOrgs, setOtherGovAssistOrgs] = React.useState<
    FormSelectChoice[] | undefined
  >();

  React.useEffect(() => {
    fetchOrganization(
      getOrgSearchUrl(
        produce(viewModel, (draft: LowIncomePersonalInfoViewModel) => {
          draft.statuses = ["OTHER_GOVERNMENT_ASSISTANCE"]; // load and cache orgs for OTHER_GOVERNMENT_ASSISTANCE as it is a InputSelect and the list of options doesn't change
        }),
        programTheme,
      ),
    )
      .then((orgList: Organization[]) => {
        if (orgList) {
          let newOrganizations: Organization[] = orgList;

          if (viewModel.countryChoice) {
            newOrganizations = orgList.filter(
              (org) => org.country === viewModel.countryChoice.value,
            );
          }
          setOtherGovAssistOrgs(newOrganizations.map(orgToOption));
        }
      })
      .catch((e) => {
        logger.error(e);
      });
  }, []);

  const handleStateChange = (selectedOrgOption: FormSelectChoice, onStateChange: Function) => {
    if (selectedOrgOption) {
      onStateChange({
        id: selectedOrgOption.value,
        name: selectedOrgOption.label,
      });
    } else {
      onStateChange({
        id: 0,
        name: "",
      });
    }
  };

  const isFieldHidden = getFieldConfigByFieldId("organization")?.hidden;

  if (isFieldHidden) {
    return null;
  }

  if (selectedStatus === "COMMUNITY_ELIGIBILITY_PROVISION") {
    return (
      <FormFieldTypeahead
        fieldId="cep-organization"
        value={viewModel.organization}
        verificationService={verificationService}
        onChange={onChange}
        placeholder={intl.formatHTMLMessage({
          id: "schoolNamePlaceholder",
          defaultMessage: "School name",
        })}
        label={<FormattedHTMLMessage id="schoolName" defaultMessage="School name" />}
        errorMessage={
          !!fieldValidationErrors.organization && (
            <FormattedHTMLMessage
              id="errorId.invalidOrganization"
              defaultMessage="You must select an organization from the list"
            />
          )
        }
        isRequired={selectedStatus === "COMMUNITY_ELIGIBILITY_PROVISION"}
        disabled={disabled}
        orgSearchUrl={getOrgSearchUrl(
          verificationService.viewModel,
          verificationService.programTheme,
        )}
      />
    );
  }

  return otherGovAssistOrgs ? (
    <FormFieldInputSelect
      fieldId="low-income"
      value={otherGovAssistOrgs.find((option) => option.value === (value && value.value))}
      refId="organization"
      onChange={(organization: FormSelectChoice) => handleStateChange(organization, onChange)}
      placeholder={
        placeholder ||
        intl.formatHTMLMessage({
          id: "lowIncomeOrganizationPlaceholder",
          defaultMessage: "Government Assistance Program",
        })
      }
      label={
        <FormattedHTMLMessage
          id="lowIncomeOrganization"
          defaultMessage="Government Assistance Program"
        />
      }
      errorMessage={
        isErrored && (
          <FormattedHTMLMessage
            id="errorId.invalidOrganization"
            defaultMessage="You must select an organization from the list"
          />
        )
      }
      isRequired={isRequired}
      showPlaceholderAndHideLabel={!!placeholder}
      options={otherGovAssistOrgs}
      containerId="sid-low-income-id"
      disabled={disabled}
    />
  ) : (
    <LoadingSpinnerComponent />
  );
};

export const LowIncomeOrganizationComponent = injectIntl(LowIncomeOrganization);
